import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, type ThemeMode } from '@sakhtar/design-system/react';

const THEME_STORAGE_KEY = 'sakhtar-client-theme-mode';

function initialThemeMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}
import { LoginPage } from './routes/auth/LoginPage.js';
import { OtpVerifyPage } from './routes/auth/OtpVerifyPage.js';
import { PasswordRecoveryPage } from './routes/auth/PasswordRecoveryPage.js';
import { SessionExpiredPage } from './routes/auth/SessionExpiredPage.js';
import { InviteAcceptPage } from './routes/auth/InviteAcceptPage.js';
import { SetPasswordPage } from './routes/auth/SetPasswordPage.js';
import { OperatorLoginPage } from './routes/internal-admin/LoginPage.js';
import { OperatorShell } from './routes/internal-admin/OperatorShell.js';
import { OrganizationsPage } from './routes/internal-admin/OrganizationsPage.js';
import {
  CreateOrganizationPage,
  OrganizationDetailPage,
} from './routes/internal-admin/OrganizationDetailPage.js';
import { PlansPage } from './routes/internal-admin/PlansPage.js';
import { AuditPage } from './routes/internal-admin/AuditPage.js';
import { BirthdaysPage } from './routes/internal-admin/BirthdaysPage.js';
import { ShortLinksPage } from './routes/internal-admin/ShortLinksPage.js';
import { BillingHandoffPage } from './routes/internal-admin/BillingHandoffPage.js';
import { AiInterestPage } from './routes/internal-admin/AiInterestPage.js';
import { AiMonitoringPage } from './routes/internal-admin/AiMonitoringPage.js';
import {
  AiFinancePage,
  AiPricingPage,
  AiQualityPage,
  AiUsagePage,
} from './routes/internal-admin/AiOpsPages.js';
import { MessengerNotificationsPage } from './routes/internal-admin/MessengerNotificationsPage.js';
import { UiKitPage as OperatorUiKitPage } from './routes/internal-admin/UiKitPage.js';
import { StartPage } from './routes/start/StartPage.js';
import { TrialOnboardingPage } from './routes/start/TrialOnboardingPage.js';
import { CheckoutPage } from './routes/start/CheckoutPage.js';
import { LoginBySlugPage } from './routes/start/LoginBySlugPage.js';
import { AdminShellPlaceholder } from './shell/AdminShellPlaceholder.js';
import { AuthenticatedShell } from './shell/AuthenticatedShell.js';
import { PrintTenantSettings } from './lib/tenantSettings.js';
import { TenantRootRedirect } from './routes/TenantRootRedirect.js';
import { SalonRoute } from './routes/salon/SalonRoute.js';
import { CrmModuleLayout } from './shell/modules/CrmModuleLayout.js';
import { FinanceModuleLayout } from './shell/modules/FinanceModuleLayout.js';
import { InventoryModuleLayout } from './shell/modules/InventoryModuleLayout.js';
import { ProcurementModuleLayout } from './shell/modules/ProcurementModuleLayout.js';
import { ProjectsModuleLayout } from './shell/modules/ProjectsModuleLayout.js';
import { MasterDataModuleLayout } from './shell/modules/MasterDataModuleLayout.js';
import { HomePage } from './routes/admin/HomePage.js';
import { SetupWizardPage } from './routes/setup/SetupWizardPage.js';
import { QuotePrintPage } from './routes/print/QuotePrintPage.js';
import { InvoicePrintPage } from './routes/print/InvoicePrintPage.js';
import { OrderPrintPage } from './routes/print/OrderPrintPage.js';
import { PurchaseOrderPrintPage } from './routes/print/PurchaseOrderPrintPage.js';
import { MovementPrintPage } from './routes/print/MovementPrintPage.js';
import { CountSheetPrintPage } from './routes/print/CountSheetPrintPage.js';
import { CustomerSalesReportPrintPage } from './routes/print/CustomerSalesReportPrintPage.js';
import { TrialBalancePrintPage } from './routes/print/TrialBalancePrintPage.js';
import { JournalVoucherPrintPage } from './routes/print/JournalVoucherPrintPage.js';
import {
  BalanceSheetPrintPage,
  IncomeStatementPrintPage,
  LedgerPrintPage,
  TrialBalanceColumnsPrintPage,
} from './routes/print/FinanceReportPrintPages.js';
import { UsersPage } from './routes/admin/UsersPage.js';
import { SubscriptionPage } from './routes/admin/SubscriptionPage.js';
import { SecurityPage } from './routes/admin/SecurityPage.js';
import { UiKitPage } from './routes/admin/UiKitPage.js';
import { ImportExportPage } from './routes/data/ImportExportPage.js';
import { AiLayout } from './routes/ai/AiLayout.js';
import { ModuleAssistantPage } from './shell/yar/ModuleAssistantPage.js';
import {
  AiChatRoute,
  AiDocumentsRoute,
  AiSettingsRoute,
  AiWalletRoute,
} from './routes/ai/switch.js';
import { MasterDataHubPage } from './routes/master-data/HubPage.js';
import { EntityListPage } from './routes/master-data/EntityListPage.js';
import { BrandAssetsPage } from './routes/master-data/BrandAssetsPage.js';
import { EntityFormPage } from './routes/master-data/EntityFormPage.js';
import { CrmHubPage } from './routes/crm/HubPage.js';
import { LeadsPage } from './routes/crm/LeadsPage.js';
import { ActivitiesPage } from './routes/crm/ActivitiesPage.js';
import { QuotesPage } from './routes/crm/QuotesPage.js';
import { QuoteDetailPage } from './routes/crm/QuoteDetailPage.js';
import { OrdersPage } from './routes/crm/OrdersPage.js';
import { OrderDetailPage } from './routes/crm/OrderDetailPage.js';
import { SalesReturnsPage } from './routes/crm/SalesReturnsPage.js';
import { SalesInvoiceDetailPage } from './routes/crm/SalesInvoiceDetailPage.js';
import { SalesInvoicesPage } from './routes/crm/SalesInvoicesPage.js';
import { CustomersPage } from './routes/crm/CustomersPage.js';
import { CustomerDetailPage } from './routes/crm/CustomerDetailPage.js';
import { CallBasePage } from './routes/crm/CallBasePage.js';
import { SmsPanelPage } from './routes/crm/SmsPanelPage.js';
import { MySalesPage } from './routes/crm/MySalesPage.js';
import { TeamPerformancePage } from './routes/crm/TeamPerformancePage.js';
import { SalesRepProfilePage } from './routes/crm/SalesRepProfilePage.js';
import { FinanceHubPage } from './routes/finance/HubPage.js';
import { AccountsPage } from './routes/finance/AccountsPage.js';
import { SalesAccountsPage } from './routes/finance/SalesAccountsPage.js';
import { AccountDetailPage } from './routes/finance/AccountDetailPage.js';
import { JournalsPage } from './routes/finance/JournalsPage.js';
import { JournalDetailPage } from './routes/finance/JournalDetailPage.js';
import { FiscalPeriodsPage } from './routes/finance/FiscalPeriodsPage.js';
import { OpenItemsPage } from './routes/finance/OpenItemsPage.js';
import { OpenItemDetailPage } from './routes/finance/OpenItemDetailPage.js';
import { TreasuryPage } from './routes/finance/TreasuryPage.js';
import { TreasuryTransactionDetailPage } from './routes/finance/TreasuryTransactionDetailPage.js';
import { ChequesPage } from './routes/finance/ChequesPage.js';
import { BankReconciliationPage } from './routes/finance/BankReconciliationPage.js';
import { VatPage } from './routes/finance/VatPage.js';
import { ModianPage } from './routes/finance/ModianPage.js';
import { BooksPage } from './routes/finance/BooksPage.js';
import { StatutoryOnly } from './routes/finance/StatutoryOnly.js';
import { ReportsPage } from './routes/finance/ReportsPage.js';
import { TrialBalancePage } from './routes/finance/TrialBalancePage.js';
import { DimensionsPage } from './routes/finance/DimensionsPage.js';
import { LedgerPage } from './routes/finance/LedgerPage.js';
import { YearEndClosingPage } from './routes/finance/YearEndClosingPage.js';
import { FixedAssetsPage } from './routes/finance/FixedAssetsPage.js';
import { CurrenciesPage } from './routes/finance/CurrenciesPage.js';
import { CostAllocationsPage } from './routes/finance/CostAllocationsPage.js';
import {
  BalanceSheetPage,
  CashFlowStatementPage,
  IncomeStatementPage,
  ProfitabilityPage,
} from './routes/finance/FinancialStatementsPages.js';
import { TrialBalanceColumnsPage } from './routes/finance/TrialBalanceColumnsPage.js';
import { InventoryHubPage } from './routes/inventory/HubPage.js';
import { StockPage } from './routes/inventory/StockPage.js';
import { CardexPage } from './routes/inventory/CardexPage.js';
import { CountSessionsPage } from './routes/inventory/CountSessionsPage.js';
import { ReconciliationPage } from './routes/inventory/ReconciliationPage.js';
import { ReceiptsPage } from './routes/inventory/ReceiptsPage.js';
import { IssuesPage } from './routes/inventory/IssuesPage.js';
import { TransfersPage } from './routes/inventory/TransfersPage.js';
import { AdjustmentsPage } from './routes/inventory/AdjustmentsPage.js';
import { ReservationsPage } from './routes/inventory/ReservationsPage.js';
import { TrackingProfilesPage } from './routes/inventory/TrackingProfilesPage.js';
import { LotsPage } from './routes/inventory/LotsPage.js';
import { SerialsPage } from './routes/inventory/SerialsPage.js';
import { UomConversionsPage } from './routes/inventory/UomConversionsPage.js';
import { InventoryReportsPage } from './routes/inventory/ReportsPage.js';
import { InventoryMovementDetailPage } from './routes/inventory/MovementDetailPage.js';
import { ProcurementHubPage } from './routes/procurement/HubPage.js';
import { RequestsPage } from './routes/procurement/RequestsPage.js';
import { RequestDetailPage } from './routes/procurement/RequestDetailPage.js';
import { OrdersPage as ProcurementOrdersPage } from './routes/procurement/OrdersPage.js';
import { OrderDetailPage as ProcurementOrderDetailPage } from './routes/procurement/OrderDetailPage.js';
import { SuppliersPage } from './routes/procurement/SuppliersPage.js';
import { ProjectsHubPage } from './routes/projects/HubPage.js';
import { ProjectListPage } from './routes/projects/ProjectListPage.js';
import { ProjectDetailPage } from './routes/projects/ProjectDetailPage.js';
import { ProjectTasksPage } from './routes/projects/TasksPage.js';
import { TaskDetailPage } from './routes/projects/TaskDetailPage.js';
import { TodosPage } from './routes/projects/TodosPage.js';
import { RemindersPage } from './routes/projects/RemindersPage.js';
import { OverduePage } from './routes/projects/OverduePage.js';
import { ProjectCommentsPage } from './routes/projects/CommentsPage.js';
import { AccountPage } from './routes/profile/AccountPage.js';
import { NotificationsPage } from './routes/mobile/NotificationsPage.js';
import { AddPage } from './routes/mobile/AddPage.js';
import { TasksPage } from './routes/mobile/TasksPage.js';
import { MenuPage } from './routes/mobile/MenuPage.js';

/**
 * Route tree mirrors docs/agents/customer-delivery-ui-route-inventory.md
 * ("NEW CLIENT ROUTE" column). The Auth family renders bare (pre-auth, no
 * shell); everything else nests under AuthenticatedShell (desktop sidebar +
 * mobile bottom nav, entitlement-gated — see shell/navConfig.ts). Routes
 * not yet built fall through to AdminShellPlaceholder's honest
 * "not built yet" state — scaffolding only, per the TL's directive, not a
 * final state for anything RUNTIME_READY/ADAPTER_REQUIRED.
 */
export function App() {
  const [mode, setMode] = useState<ThemeMode>(initialThemeMode);

  const handleModeChange = (next: ThemeMode) => {
    setMode(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Best-effort persistence only — the toggle still works for this
      // page view even if storage is blocked.
    }
  };

  return (
    <ThemeProvider mode={mode} onModeChange={handleModeChange}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<StartPage />} />
          <Route path="/start" element={<StartPage />} />
          <Route path="/start/trial" element={<TrialOnboardingPage />} />
          <Route path="/start/login" element={<LoginBySlugPage />} />
          <Route path="/start/checkout" element={<CheckoutPage />} />
          <Route path="/demo" element={<Navigate to="/t/demo/login" replace />} />
          <Route path="/t/demo" element={<Navigate to="/t/demo/login" replace />} />

          <Route path="/internal/admin/login" element={<OperatorLoginPage />} />
          <Route path="/internal/admin" element={<OperatorShell />}>
            <Route index element={<Navigate to="organizations" replace />} />
            <Route path="organizations" element={<OrganizationsPage />} />
            <Route path="organizations/new" element={<CreateOrganizationPage />} />
            <Route path="organizations/:organizationRef" element={<OrganizationDetailPage />} />
            <Route path="plans" element={<PlansPage />} />
            <Route path="ai-assistant-interest" element={<AiInterestPage />} />
            <Route path="ai/pricing" element={<AiPricingPage />} />
            <Route path="ai/usage" element={<AiUsagePage />} />
            <Route path="ai/finance" element={<AiFinancePage />} />
            <Route path="ai/quality" element={<AiQualityPage />} />
            <Route path="ai/monitoring" element={<AiMonitoringPage />} />
            <Route path="notifications" element={<MessengerNotificationsPage />} />
            <Route path="billing" element={<BillingHandoffPage />} />
            <Route path="birthdays" element={<BirthdaysPage />} />
            <Route path="short-links" element={<ShortLinksPage />} />
            <Route path="audit" element={<AuditPage />} />
            <Route path="ui-kit" element={<OperatorUiKitPage />} />
          </Route>

          <Route path="/t/:slug/login" element={<LoginPage />} />
          <Route path="/t/:slug/login/otp" element={<OtpVerifyPage />} />
          <Route path="/t/:slug/login/recovery" element={<PasswordRecoveryPage />} />
          <Route path="/t/:slug/password" element={<SetPasswordPage />} />
          <Route path="/t/:slug/session-expired" element={<SessionExpiredPage />} />
          <Route path="/t/:slug/auth/invite/accept" element={<InviteAcceptPage />} />
          {/* Standalone full-bleed onboarding shell (Phase 1 of the /setup
              reconciliation) — not nested under AuthenticatedShell's full
              app sidebar/topbar. See SetupWizardPage.tsx's own doc comment. */}
          <Route path="/t/:slug/setup" element={<SetupWizardPage />} />
          {/* Dedicated tenant application (beauty-salon showcase): its own
              full-bleed shell, no ERP sidebar. Entitlement + authentication
              are enforced by SalonRoute against the server before anything
              salon-related is loaded. */}
          <Route path="/t/:slug/salon/*" element={<SalonRoute />} />
          {/* Standalone A4-landscape print documents — same "no
              AuthenticatedShell wrapper" pattern as /setup above; a 401 on
              their own API calls already redirects to login at the
              apiClient layer. See routes/print/PrintChrome.tsx. */}
          <Route
            path="/t/:slug/crm/quotes/:ref/print"
            element={
              <PrintTenantSettings>
                <QuotePrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/crm/invoices/:invoiceId/print"
            element={
              <PrintTenantSettings>
                <InvoicePrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/crm/orders/:ref/print"
            element={
              <PrintTenantSettings>
                <OrderPrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/procurement/orders/:ref/print"
            element={
              <PrintTenantSettings>
                <PurchaseOrderPrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/inventory/movements/:movementId/print"
            element={
              <PrintTenantSettings>
                <MovementPrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/inventory/count-sessions/:sessionId/print"
            element={
              <PrintTenantSettings>
                <CountSheetPrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/crm/reports/customers/print"
            element={
              <PrintTenantSettings>
                <CustomerSalesReportPrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/finance/reports/ledger/print"
            element={
              <PrintTenantSettings>
                <LedgerPrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/finance/reports/trial-balance-columns/print"
            element={
              <PrintTenantSettings>
                <TrialBalanceColumnsPrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/finance/reports/income-statement/print"
            element={
              <PrintTenantSettings>
                <IncomeStatementPrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/finance/reports/balance-sheet/print"
            element={
              <PrintTenantSettings>
                <BalanceSheetPrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/finance/reports/trial-balance/print"
            element={
              <PrintTenantSettings>
                <TrialBalancePrintPage />
              </PrintTenantSettings>
            }
          />
          <Route
            path="/t/:slug/print/journals/:journalId"
            element={
              <PrintTenantSettings>
                <JournalVoucherPrintPage />
              </PrintTenantSettings>
            }
          />

          <Route path="/t/:slug" element={<AuthenticatedShell />}>
            <Route index element={<TenantRootRedirect />} />
            <Route path="admin" element={<HomePage />} />
            <Route path="admin/users" element={<UsersPage />} />
            <Route path="admin/subscription" element={<SubscriptionPage />} />
            <Route path="admin/security" element={<SecurityPage />} />
            <Route path="admin/ui-kit" element={<UiKitPage />} />
            <Route path="data" element={<ImportExportPage />} />
            <Route path="ai" element={<AiLayout />}>
              <Route index element={<AiChatRoute />} />
              <Route path="documents" element={<AiDocumentsRoute />} />
              <Route path="wallet" element={<AiWalletRoute />} />
              <Route path="settings" element={<AiSettingsRoute />} />
            </Route>

            <Route path="master-data" element={<MasterDataModuleLayout />}>
              <Route index element={<MasterDataHubPage />} />
              <Route path="brand-assets" element={<BrandAssetsPage />} />
              <Route path=":entity" element={<EntityListPage />} />
              <Route path=":entity/new" element={<EntityFormPage />} />
              <Route path=":entity/:id" element={<EntityFormPage />} />
            </Route>

            <Route path="crm" element={<CrmModuleLayout />}>
              <Route path="assistant" element={<ModuleAssistantPage moduleId="crm" />} />
              <Route index element={<CrmHubPage />} />
              <Route path="leads" element={<LeadsPage />} />
              <Route path="opportunities" element={<Navigate to="../leads" replace />} />
              <Route path="activities" element={<ActivitiesPage />} />
              <Route path="quotes" element={<QuotesPage />} />
              <Route path="quotes/:ref" element={<QuoteDetailPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="orders/:ref" element={<OrderDetailPage />} />
              <Route path="returns" element={<SalesReturnsPage />} />
              <Route path="invoices" element={<SalesInvoicesPage />} />
              <Route path="invoices/:invoiceId" element={<SalesInvoiceDetailPage />} />
              <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/:ref" element={<CustomerDetailPage />} />
              <Route path="call-base" element={<CallBasePage />} />
              <Route path="sms-panel" element={<SmsPanelPage />} />
              <Route path="my-sales" element={<MySalesPage />} />
              <Route path="team" element={<TeamPerformancePage />} />
              <Route path="team/:salespersonId" element={<SalesRepProfilePage />} />
              <Route path="commission" element={<Navigate to="../team" replace />} />
            </Route>

            <Route path="finance" element={<FinanceModuleLayout />}>
              <Route path="assistant" element={<ModuleAssistantPage moduleId="finance" />} />
              <Route index element={<FinanceHubPage />} />
              <Route path="ar" element={<OpenItemsPage kind="ar" />} />
              <Route path="ar/:openItemId" element={<OpenItemDetailPage kind="ar" />} />
              <Route path="ap" element={<OpenItemsPage kind="ap" />} />
              <Route path="ap/:openItemId" element={<OpenItemDetailPage kind="ap" />} />
              <Route path="treasury" element={<TreasuryPage />} />
              <Route
                path="treasury/transactions/:treasuryTransactionId"
                element={<TreasuryTransactionDetailPage />}
              />
              <Route path="cheques" element={<ChequesPage />} />
              <Route path="treasury/cheques" element={<ChequesPage />} />
              <Route path="bank-recon" element={<BankReconciliationPage />} />
              <Route path="sales-accounts" element={<SalesAccountsPage />} />
              <Route
                path="vat"
                element={
                  <StatutoryOnly feature="ارزش افزوده">
                    <VatPage />
                  </StatutoryOnly>
                }
              />
              <Route
                path="modian"
                element={
                  <StatutoryOnly feature="سامانه مودیان">
                    <ModianPage />
                  </StatutoryOnly>
                }
              />
              <Route path="books" element={<BooksPage />} />
              <Route path="accounts" element={<AccountsPage />} />
              <Route path="accounts/:accountId" element={<AccountDetailPage />} />
              <Route path="dimensions" element={<DimensionsPage />} />
              <Route path="fixed-assets" element={<FixedAssetsPage />} />
              <Route path="currencies" element={<CurrenciesPage />} />
              <Route path="cost-allocations" element={<CostAllocationsPage />} />
              <Route path="journals" element={<JournalsPage />} />
              <Route path="journals/:journalId" element={<JournalDetailPage />} />
              <Route path="fiscal-periods" element={<FiscalPeriodsPage />} />
              <Route path="year-end" element={<YearEndClosingPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/trial-balance" element={<TrialBalancePage />} />
              <Route path="reports/trial-balance-columns" element={<TrialBalanceColumnsPage />} />
              <Route path="reports/ledger" element={<LedgerPage />} />
              <Route path="reports/income-statement" element={<IncomeStatementPage />} />
              <Route path="reports/balance-sheet" element={<BalanceSheetPage />} />
              <Route path="reports/cash-flow" element={<CashFlowStatementPage />} />
              <Route path="reports/profitability" element={<ProfitabilityPage />} />
            </Route>

            <Route path="inventory" element={<InventoryModuleLayout />}>
              <Route path="assistant" element={<ModuleAssistantPage moduleId="inventory" />} />
              <Route index element={<InventoryHubPage />} />
              <Route path="stock" element={<StockPage />} />
              <Route path="cardex" element={<CardexPage />} />
              <Route path="count-sessions" element={<CountSessionsPage />} />
              <Route path="count-sessions/:sessionId" element={<CountSessionsPage />} />
              <Route path="reconciliation" element={<ReconciliationPage />} />
              <Route path="receipts" element={<ReceiptsPage />} />
              <Route path="issues" element={<IssuesPage />} />
              <Route path="transfers" element={<TransfersPage />} />
              <Route path="adjustments" element={<AdjustmentsPage />} />
              <Route path="counts" element={<AdjustmentsPage />} />
              <Route path="reservations" element={<ReservationsPage />} />
              <Route path="tracking-profiles" element={<TrackingProfilesPage />} />
              <Route path="lots" element={<LotsPage />} />
              <Route path="serials" element={<SerialsPage />} />
              <Route path="uom-conversions" element={<UomConversionsPage />} />
              <Route path="reports" element={<InventoryReportsPage />} />
              <Route path="movements" element={<Navigate to="receipts" replace />} />
              <Route path="movements/:movementId" element={<InventoryMovementDetailPage />} />
            </Route>

            <Route path="procurement" element={<ProcurementModuleLayout />}>
              <Route path="assistant" element={<ModuleAssistantPage moduleId="procurement" />} />
              <Route index element={<ProcurementHubPage />} />
              <Route path="requests" element={<RequestsPage />} />
              <Route path="requests/:ref" element={<RequestDetailPage />} />
              <Route path="orders" element={<ProcurementOrdersPage />} />
              <Route path="orders/:ref" element={<ProcurementOrderDetailPage />} />
              <Route path="suppliers" element={<SuppliersPage />} />
            </Route>

            <Route path="projects" element={<ProjectsModuleLayout />}>
              <Route path="assistant" element={<ModuleAssistantPage moduleId="projects" />} />
              <Route index element={<ProjectsHubPage />} />
              <Route path="list" element={<ProjectListPage />} />
              <Route path="list/:ref" element={<ProjectDetailPage />} />
              <Route path="tasks" element={<ProjectTasksPage />} />
              <Route path="tasks/:ref" element={<TaskDetailPage />} />
              <Route path="todos" element={<TodosPage />} />
              <Route path="reminders" element={<RemindersPage />} />
              <Route path="overdue" element={<OverduePage />} />
              <Route path="comments" element={<ProjectCommentsPage />} />
            </Route>

            <Route path="account" element={<AccountPage />} />
            <Route path="mobile/notifications" element={<NotificationsPage />} />
            <Route path="mobile/add" element={<AddPage />} />
            <Route path="mobile/tasks" element={<TasksPage />} />
            <Route path="mobile/menu" element={<MenuPage />} />

            <Route path="*" element={<AdminShellPlaceholder />} />
          </Route>

          <Route path="*" element={<Navigate to="/start" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
